import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { CruiseModel } from 'api-hooks/cruise';
import { NavigationRoutes } from 'common/constants';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { SeparatorVertical } from 'components/styles';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import { CardContainer } from 'mantine-components/container/card-container';
import {
  HalfGridContainer,
  OneQuarterGridContainer,
  OneThirdGridContainer,
} from 'mantine-components/container/grid-container';
import Input from 'mantine-components/elements';
import Text from 'mantine-components/widget/text';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UniskaiFormHeaderRef } from 'templates/form/uniskai-form-header';
import UniskaiFormTemplate from 'templates/form/uniskai-form-template';

import CabinSection from './cabin-section';
import { CruiseFormSchema, CruiseFormType } from './form-type';
import ShipSection from './ship-section';
import useCruiseDefaultValues from '../hooks/use-cruise-default-values';

interface CruiseFormProps {
  query?: UseQueryResult<ApiResult<CruiseModel>, ApiError>;
  onSubmit: (
    input: CruiseFormType,
    form: UseFormReturn<CruiseFormType>,
  ) => Promise<CruiseModel>;
  saveMutation?: UseMutationResult;
}

export default function CruiseForm(props: CruiseFormProps) {
  const { t } = useTranslation();
  const cruise = props.query?.data?.data;
  const defaultValues = useCruiseDefaultValues(cruise);
  const permission = useGetCrudPermission('cruises');
  const { deleteMutate, onDelete } = useDestroyAction('cruises');

  const titleRef = React.useRef<UniskaiFormHeaderRef>(null);
  React.useEffect(() => {
    let title = t(`pathname:${NavigationRoutes.CruisesCreate}`);
    if (cruise) {
      title = t(`pathname:${NavigationRoutes.CruisesView}`, {
        extra: cruise.name,
      });
    }
    titleRef.current?.setTitle(title);
  }, [t, cruise]);

  return (
    <UniskaiFormTemplate
      formProps={{
        defaultEditable: !cruise,
        defaultValues,
        resolver: yupResolver(CruiseFormSchema()),
        onSubmit: props.onSubmit as any,
      }}
      customs={[
        {
          type: 'delete',
          loading: deleteMutate.isLoading,
          onClick: () => onDelete(cruise?.id),
          show: permission.delete,
        },
        {
          type: 'edit',
          show: permission.update,
        },
      ]}
      data={cruise}
      query={props.query}
      titleRef={titleRef}
      saveMutation={props.saveMutation}
    >
      <CardContainer>
        <Text variant="headingXSmall">{t('cruise:cruise_detail')}</Text>
        <SeparatorVertical $margin={8} />
        <HalfGridContainer>
          <Input
            type="text"
            name="code"
            label={t('common:code')}
            placeholder={t('common:code')}
            required
          />
          <Input
            type="text"
            name="name"
            label={t('cruise:cruise_line_name')}
            placeholder={t('cruise:cruise_line_name')}
            required
          />
        </HalfGridContainer>
      </CardContainer>
      <SeparatorVertical $margin={24} />
      <CardContainer>
        <Text variant="headingXSmall">{t('cruise:contact_information')}</Text>
        <SeparatorVertical $margin={8} />
        <OneThirdGridContainer>
          <Input
            type="text"
            name="email"
            label={t('common:email')}
            placeholder={t('common:email')}
            required
          />
          <Input
            type="text"
            name="phone"
            label={t('cruise:tel_phone')}
            placeholder={t('cruise:tel_phone')}
            required
          />
          <Input
            type="text"
            name="phone_2"
            label={t('cruise:cellphone_number')}
            placeholder={t('cruise:cellphone_number')}
            required
          />
        </OneThirdGridContainer>
      </CardContainer>
      <SeparatorVertical $margin={24} />
      <CardContainer>
        <Text variant="headingXSmall">{t('cruise:contact_person')}</Text>
        <SeparatorVertical $margin={8} />
        <OneQuarterGridContainer>
          <Input
            type="text"
            name="contact.name"
            label={t('common:name')}
            placeholder={t('common:name')}
            required
          />
          <Input
            type="text"
            name="contact.email"
            label={t('common:email')}
            placeholder={t('common:email')}
            required
          />
          <Input
            type="text"
            name="contact.phone"
            label={t('cruise:tel_phone')}
            placeholder={t('cruise:tel_phone')}
            required
          />
          <Input
            type="text"
            name="contact.description"
            label={t('common:description')}
            placeholder={t('common:description')}
          />
        </OneQuarterGridContainer>
      </CardContainer>
      <SeparatorVertical $margin={24} />
      <ShipSection />
      <SeparatorVertical $margin={24} />
      <CabinSection />
      <SeparatorVertical $margin={24} />
    </UniskaiFormTemplate>
  );
}
